/**
 * features/finance/SupplierAging.tsx
 * ------------------------------------------------------------------
 * WHAT THE SCHOOL OWES, AND HOW LONG IT HAS OWED IT.
 *
 * The debtors side of this system has been answerable for a year: the
 * school knows to the pula what families owe it. The creditors side has
 * not. Vouchers could be raised, checked, approved and paid, and nobody
 * could open one page and say what was outstanding to whom — which means
 * the trade creditors figure in the trial balance had nothing to be
 * checked against, and a supplier ringing to chase an invoice was
 * answered from memory.
 *
 * AGE IS THE POINT, NOT THE TOTAL. P200,000 owed across thirty bills
 * raised last week is a school paying on terms. The same P200,000 sitting
 * in "over 90 days" is a school with a supplier about to stop delivering
 * and a reputation problem it has not noticed. The buckets are the
 * headline for that reason, and the row is sorted by what is owed because
 * that is the order the pay-run decision is made in.
 *
 * ONE ROW PER CREDITOR, EXPANDABLE TO THE BILLS. A summary nobody can
 * drill into gets re-derived in a spreadsheet, and the spreadsheet is what
 * the meeting then argues about.
 *
 * A PAYEE WITH NO REGISTER ENTRY STILL APPEARS, marked as unregistered.
 * Dropping ad-hoc reimbursements would understate the liability, and
 * showing them is the visible nudge to register a supplier properly.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  Wallet, ChevronDown, ChevronRight, AlertTriangle, UserPlus, Clock,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import {
  VOUCHER_COLLECTIONS, creditorsBySupplier, tradeCreditorsTotal, daysOverdue,
  type Voucher, type CreditorSummary,
} from '@/lib/vouchers';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, ExportBar, glassFieldClass,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Overdue reads as a warning only once it actually is one. */
function ageTone(days: number): { text: string; chip: string } {
  if (days <= 0) return { text: 'text-gray-600 dark:text-gray-300', chip: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
  if (days <= 30) return { text: 'text-gray-700 dark:text-gray-200', chip: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' };
  if (days <= 60) return { text: 'text-amber-700 dark:text-amber-300', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' };
  if (days <= 90) return { text: 'text-orange-700 dark:text-orange-300', chip: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' };
  return { text: 'text-red-700 dark:text-red-300', chip: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' };
}

const ageLabel = (days: number) =>
  days <= 0 ? `Due in ${Math.abs(days)}d` : `${days}d overdue`;

export default function SupplierAging() {
  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (snap) => setVouchers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);

  const rows = useMemo(
    () => (vouchers ? creditorsBySupplier(vouchers) : []),
    [vouchers],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  /* The control total comes from the same function the trial balance uses,
     so this page and the control-accounts page cannot drift apart. */
  const control = useMemo(
    () => (vouchers ? tradeCreditorsTotal(vouchers) : 0),
    [vouchers],
  );

  const totals = useMemo(() => {
    const labels = rows[0]?.buckets.map((b) => b.label) ?? [];
    return labels.map((label) => ({
      label,
      total: rows.reduce(
        (a, r) => a + (r.buckets.find((b) => b.label === label)?.total ?? 0), 0,
      ),
    }));
  }, [rows]);

  const owed = rows.reduce((a, r) => a + r.total, 0);
  const overdue = rows.reduce(
    (a, r) => a + r.buckets.filter((b) => b.label !== 'Not yet due').reduce((s, b) => s + b.total, 0),
    0,
  );
  const worst = rows.reduce((a, r) => Math.max(a, r.oldestDays), 0);

  function exportCsv() {
    const head = ['Creditor', 'Registered', 'Bills', 'Oldest (days)',
      ...(totals.map((t) => t.label)), 'Total'];
    const lines = filtered.map((r) => [
      `"${r.name.replace(/"/g, '""')}"`,
      r.supplierId ? 'yes' : 'no',
      r.vouchers.length,
      r.oldestDays,
      ...r.buckets.map((b) => b.total.toFixed(2)),
      r.total.toFixed(2),
    ].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `creditors-aging-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (vouchers === null) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={Wallet}
        tone="navy"
        title="Creditors Aging"
        subtitle="What the school owes its suppliers, and for how long"
        actions={<ExportBar onCsv={exportCsv} />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatChip tone="navy" icon={Wallet} value={money(owed)} label="Owed in total" />
        <StatChip tone={overdue > 0 ? 'amber' : 'slate'} icon={Clock}
          value={money(overdue)} label="Past its due date" />
        <StatChip tone={worst > 90 ? 'rose' : 'slate'} icon={AlertTriangle}
          value={worst > 0 ? `${worst}d` : '—'} label="Oldest unpaid bill" />
        <StatChip tone="slate" value={String(filtered.length)} label="Creditors" />
      </div>

      {/* The control total, stated rather than implied. If this and the sum of
          the rows ever differ, one of them is wrong and the reader should
          find out here rather than in an audit. */}
      {Math.abs(control - owed) > 0.005 && (
        <SoftCard className="mb-4 border-l-4 border-amber-400">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle size={15} className="inline mr-1 -mt-0.5" />
            The trade-creditors control total is <strong>{money(control)}</strong>, but these rows
            add to <strong>{money(owed)}</strong>. The difference is vouchers settling a payable
            that was raised elsewhere — payroll and statutory deductions, which the payslip
            journal has already credited.
          </p>
        </SoftCard>
      )}

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader icon={Clock} title="How old the debt is" tone="navy"
          subtitle="Every approved, unpaid voucher, bucketed by how far past due it is" />
        <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {totals.map((t) => {
            const tone = ageTone(t.label === 'Not yet due' ? 0
              : t.label === 'Over 90 days' ? 120
              : t.label.startsWith('61') ? 75 : t.label.startsWith('31') ? 45 : 15);
            return (
              <div key={t.label} className="rounded-lg border border-gray-100 dark:border-gray-700/70 p-3">
                <p className={`text-lg font-bold tabular-nums ${tone.text}`}>{money(t.total)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{t.label}</p>
              </div>
            );
          })}
        </div>
      </SoftCard>

      <SoftCard pad="p-0">
        <PanelHeader
          icon={Wallet} title="By creditor" tone="navy"
          subtitle="Largest first — the order a pay run is decided in"
          right={(
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a creditor…"
              className={glassFieldClass('max-w-[200px]')}
            />
          )}
        />

        {filtered.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            {rows.length === 0
              ? 'Nothing is outstanding. Every approved voucher has been paid.'
              : 'No creditor matches that search.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Creditor</th>
                  <th className="px-2 font-semibold text-right">Bills</th>
                  <th className="px-2 font-semibold text-right">Oldest</th>
                  <th className="px-2 font-semibold text-right">Owed</th>
                  <th className="px-2 pr-4 w-8" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <CreditorRow
                    key={r.key} row={r}
                    expanded={open === r.key}
                    onToggle={() => setOpen(open === r.key ? null : r.key)}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-bold">
                  <td className="py-2.5 pl-4 pr-2">Total owed</td>
                  <td className="px-2 text-right tabular-nums">
                    {filtered.reduce((a, r) => a + r.vouchers.length, 0)}
                  </td>
                  <td />
                  <td className="px-2 text-right tabular-nums">
                    {money(filtered.reduce((a, r) => a + r.total, 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}

function CreditorRow({
  row, expanded, onToggle,
}: { row: CreditorSummary; expanded: boolean; onToggle: () => void }) {
  const tone = ageTone(row.oldestDays);
  return (
    <>
      <tr
        onClick={onToggle}
        className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
      >
        <td className="py-2.5 pl-4 pr-2">
          <span className="font-medium text-gray-900 dark:text-white">{row.name}</span>
          {/* Not a fault, but worth seeing: an unregistered payee has no
              verified banking behind it. */}
          {!row.supplierId && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              <UserPlus size={10} /> not registered
            </span>
          )}
        </td>
        <td className="px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
          {row.vouchers.length}
        </td>
        <td className="px-2 text-right">
          <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${tone.chip}`}>
            {ageLabel(row.oldestDays)}
          </span>
        </td>
        <td className={`px-2 text-right tabular-nums font-semibold ${tone.text}`}>
          {money(row.total)}
        </td>
        <td className="px-2 pr-4 text-gray-400">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50/60 dark:bg-gray-900/30">
          <td colSpan={5} className="px-4 py-3">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400">
                  <th className="py-1 pr-3 font-semibold">Voucher</th>
                  <th className="pr-3 font-semibold">For</th>
                  <th className="pr-3 font-semibold">Due</th>
                  <th className="pr-3 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {row.vouchers.map((v) => {
                  const d = daysOverdue(v);
                  return (
                    <tr key={v.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 pr-3 font-mono text-xs text-gray-600 dark:text-gray-300">
                        {v.voucherNo}
                      </td>
                      <td className="pr-3 text-gray-700 dark:text-gray-200 truncate max-w-[280px]">
                        {v.narration}
                      </td>
                      <td className={`pr-3 tabular-nums ${ageTone(d).text}`}>
                        {v.dueDate?.slice(0, 10) ?? <span className="text-gray-400">undated</span>}
                      </td>
                      <td className="pr-3 text-right tabular-nums font-medium">{money(v.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
